import { useEffect, useState } from "react";

const BASE = "https://support.gs-group.fr/api";

// Cache mémoire partagé entre tous les <Logo/> de l'appli : un seul fetch réseau,
// tous les composants montés ensuite lisent la valeur déjà résolue.
let brandingCache = null;
let brandingPromise = null;

function fetchBranding() {
  if (brandingCache) return Promise.resolve(brandingCache);
  if (!brandingPromise) {
    brandingPromise = fetch(`${BASE}/settings/branding`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { brandingCache = data || {}; return brandingCache; })
      .catch(() => { brandingCache = {}; return brandingCache; });
  }
  return brandingPromise;
}

// À appeler après un upload/suppression de logo réussi pour forcer tous les <Logo/>
// affichés à l'écran à se resynchroniser sans recharger la page.
export function invalidateBrandingCache() {
  brandingCache = null;
  brandingPromise = null;
}

export function useBranding() {
  const [branding, setBranding] = useState(brandingCache);
  useEffect(() => {
    let alive = true;
    fetchBranding().then(data => { if (alive) setBranding(data); });
    return () => { alive = false; };
  }, []);
  return branding;
}

// Icône de secours (dôme de vidéosurveillance GS Group) utilisée tant qu'aucun logo
// personnalisé n'a été uploadé, ou si l'upload échoue. Conçue pour rester lisible
// directement sur un fond sombre (pas besoin de chip de contraste).
function DefaultMark({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="16" cy="19" rx="13" ry="9" fill="#c8d0da"/>
      <path d="M3,19 Q3,6 16,6 Q29,6 29,19 Z" fill="#dde3ea"/>
      <circle cx="16" cy="17" r="7" fill="#1a2030"/>
      <circle cx="16" cy="17" r="5.5" fill="#1e5c8a"/>
      <circle cx="16" cy="17" r="4" fill="#2a8ab8"/>
      <circle cx="14" cy="15" r="1.5" fill="white" opacity="0.35"/>
      <circle cx="16" cy="17" r="1.5" fill="#0a1828"/>
      <circle cx="6" cy="19" r="1.2" fill="#9aa0aa"/>
      <circle cx="26" cy="19" r="1.2" fill="#9aa0aa"/>
    </svg>
  );
}

// Un logo uploadé par l'utilisateur est le plus souvent un texte sombre sur fond
// transparent (cas de figure standard) : posé tel quel sur un fond sombre (sidebar,
// écran de connexion), il devient illisible. On le pose donc systématiquement sur une
// plaque blanche arrondie, quelle que soit la palette du logo — seule garantie de
// contraste qui ne dépend pas de connaître les couleurs du fichier uploadé.
export function ContrastChip({ children, paddingV = 6, paddingH = 10, radius = 10, style }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      background: "#fff", borderRadius: radius,
      padding: `${paddingV}px ${paddingH}px`,
      boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
      ...style,
    }}>
      {children}
    </div>
  );
}

// Composant universel de logo : mise à l'échelle automatique (object-contain) avec
// des contraintes strictes de max-height/max-width pour ne jamais déformer le layout,
// et une plaque de contraste sur les fonds sombres. Retombe sur l'icône par défaut GS
// Group tant qu'aucun logo n'a été uploadé dans Réglages > Identité visuelle.
//
// variant: "sidebar-dark" | "header-light" | "login" | "preview"
// collapsed: pour "sidebar-dark" uniquement — bascule sur la déclinaison carrée (icône)
//            quand la sidebar est réduite, plutôt que d'écraser le logo horizontal.
export default function Logo({ variant = "header-light", collapsed = false, size, className, style, fallback = "mark" }) {
  const branding = useBranding();
  const name = branding?.company_name || "GS GROUP";

  if (variant === "sidebar-dark") {
    const h = size || (collapsed ? 22 : 26);
    // En réduit, on n'utilise QUE la déclinaison carrée générée côté serveur (jamais le
    // logo complet) : un logo large écrasé dans une icône minuscule devient illisible.
    // Si l'upload n'a pas produit d'icône (logo non "carré-compatible"), on retombe sur
    // le pictogramme de marque par défaut plutôt que d'afficher un timbre illisible.
    const src = collapsed ? branding?.logo_icon_url : branding?.logo_url;
    return (
      <div className={className} style={{ display: "inline-flex", flexShrink: 0, ...style }}>
        {src
          ? <ContrastChip radius={collapsed ? 10 : 9} paddingV={collapsed ? 7 : 6} paddingH={collapsed ? 7 : 10}>
              <img src={src} alt={name} style={{ maxHeight: h, maxWidth: collapsed ? h : 150, objectFit: "contain", display: "block" }}/>
            </ContrastChip>
          : <div style={{ width: h + 14, height: h + 14, borderRadius: 10, background: "linear-gradient(135deg,#1a3d6e,#071428)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <DefaultMark size={h}/>
            </div>
        }
      </div>
    );
  }

  if (variant === "login") {
    const h = size || 38;
    const src = branding?.logo_url;
    return (
      <div className={className} style={{ display: "inline-flex", flexShrink: 0, ...style }}>
        {src
          ? <ContrastChip radius={12} paddingV={9} paddingH={14}>
              <img src={src} alt={name} style={{ maxHeight: h, maxWidth: 220, objectFit: "contain", display: "block" }}/>
            </ContrastChip>
          : <DefaultMark size={h + 6}/>
        }
      </div>
    );
  }

  // "header-light" / "preview" : fond clair, pas besoin de plaque de contraste.
  const h = size || (variant === "preview" ? 56 : 30);
  const src = branding?.logo_url;
  if (!src && fallback === "none") return null;
  return (
    <div className={className} style={{ display: "inline-flex", alignItems: "center", flexShrink: 0, ...style }}>
      {src
        ? <img src={src} alt={name} style={{ maxHeight: h, maxWidth: variant === "preview" ? 240 : 160, objectFit: "contain", display: "block" }}/>
        : <DefaultMark size={h}/>
      }
    </div>
  );
}
